<template>
  <div ref="containerRef" class="d3tree-container" style="width:100%;height:100%;overflow:hidden;">
    <svg ref="svgRef" style="width:100%;height:100%;"></svg>
  </div>
</template>

<script>
import * as d3 from 'd3'

export default {
  name: 'D3Tree',
  props: {
    data: {
      type: Object,
      default: null
    },
    nodeText: {
      type: String,
      default: 'name'
    },
    type: {
      type: String,
      default: 'tree' // 'tree' | 'cluster'
    },
    layoutType: {
      type: String,
      default: 'horizontal' // 'horizontal' | 'vertical'
    },
    nodeTextDisplay: {
      type: String,
      default: 'all'
    },
    linkLayout: {
      type: String,
      default: 'bezier'
    },
    strokeWidth: {
      type: Number,
      default: 0.5
    },
    radius: {
      type: Number,
      default: 4.5
    },
    duration: {
      type: Number,
      default: 100
    },
    leafTextMargin: {
      type: Number,
      default: 8
    },
    nodeTextMargin: {
      type: Number,
      default: 8
    },
    zoomable: {
      type: Boolean,
      default: true
    },
    maxZoom: {
      type: Number,
      default: 9
    },
    minZoom: {
      type: Number,
      default: 1
    },
    marginX: {
      type: Number,
      default: 40
    },
    marginY: {
      type: Number,
      default: 25
    }
  },
  emits: ['clickedText', 'expand'],
  data () {
    return {
      internaldata: {
        root: null,
        nodes: null,
        links: null
      },
      _svg: null,
      _g: null,
      _zoom: null
    }
  },
  mounted () {
    if (this.data) {
      this._render()
    }
    this._resizeObserver = new ResizeObserver(() => {
      if (this.data) this._render()
    })
    this._resizeObserver.observe(this.$refs.containerRef)
  },
  beforeUnmount () {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
    }
  },
  watch: {
    data (newVal) {
      if (newVal) this._render()
    },
    type () { if (this.data) this._render() },
    layoutType () { if (this.data) this._render() },
    linkLayout () { if (this.data) this._render() }
  },
  methods: {
    _getSize () {
      const el = this.$refs.containerRef
      return {
        width: el ? el.clientWidth || 800 : 800,
        height: el ? el.clientHeight || 600 : 600
      }
    },
    _render () {
      const svg = d3.select(this.$refs.svgRef)
      svg.selectAll('*').remove()

      const { width, height } = this._getSize()
      const isHorizontal = this.layoutType === 'horizontal'
      const treeWidth = isHorizontal ? height - this.marginY * 2 : width - this.marginX * 2
      const treeHeight = isHorizontal ? width - this.marginX * 2 : height - this.marginY * 2

      const layout = this.type === 'cluster'
        ? d3.cluster().size([treeWidth, treeHeight])
        : d3.tree().size([treeWidth, treeHeight])

      const root = d3.hierarchy(this.data)

      // Collapse nodes based on initial state
      root.descendants().forEach(d => {
        if (d._children) {
          d.children = d._children
          d._children = null
        }
      })

      layout(root)

      this.internaldata = { root, nodes: root.descendants(), links: root.links() }

      const g = svg.append('g')
        .attr('transform', isHorizontal
          ? `translate(${this.marginX},${this.marginY})`
          : `translate(${this.marginX},${this.marginY})`)

      this._g = g
      this._svg = svg

      if (this.zoomable) {
        const zoom = d3.zoom()
          .scaleExtent([this.minZoom, this.maxZoom])
          .on('zoom', (event) => {
            g.attr('transform', event.transform)
          })
        svg.call(zoom)
        this._zoom = zoom
      }

      const linkGen = isHorizontal
        ? (this.linkLayout === 'orthogonal'
            ? d3.linkHorizontal().x(d => d.y).y(d => d.x)
            : d3.linkHorizontal().x(d => d.y).y(d => d.x))
        : (this.linkLayout === 'orthogonal'
            ? d3.linkVertical().x(d => d.x).y(d => d.y)
            : d3.linkVertical().x(d => d.x).y(d => d.y))

      g.selectAll('.link')
        .data(root.links())
        .enter().append('path')
        .attr('class', 'link')
        .attr('fill', 'none')
        .attr('stroke', '#ccc')
        .attr('stroke-width', this.strokeWidth)
        .attr('d', linkGen)

      const node = g.selectAll('.node')
        .data(root.descendants())
        .enter().append('g')
        .attr('class', d => `node ${d.children ? 'node--internal' : 'node--leaf'}`)
        .attr('transform', d => isHorizontal
          ? `translate(${d.y},${d.x})`
          : `translate(${d.x},${d.y})`)
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          this.$emit('clickedText', { element: d, data: d.data })
        })

      node.append('circle')
        .attr('r', this.radius)
        .attr('fill', d => d.children ? '#555' : '#999')

      const showText = (d) => {
        if (this.nodeTextDisplay === 'all') return true
        if (this.nodeTextDisplay === 'leaves') return !d.children
        if (this.nodeTextDisplay === 'extremities') return !d.children || !d.parent
        return true
      }

      node.filter(d => showText(d))
        .append('text')
        .attr('dy', '0.31em')
        .attr('x', d => {
          if (isHorizontal) return d.children ? -this.nodeTextMargin : this.leafTextMargin
          return 0
        })
        .attr('y', d => {
          if (!isHorizontal) return d.children ? -this.nodeTextMargin : this.leafTextMargin
          return 0
        })
        .attr('text-anchor', d => {
          if (isHorizontal) return d.children ? 'end' : 'start'
          return 'middle'
        })
        .style('font-size', '11px')
        .style('font-family', 'sans-serif')
        .text(d => d.data[this.nodeText] || d.data.name || '')
        .on('click', (event, d) => {
          event.stopPropagation()
          this.$emit('clickedText', { element: d, data: d.data })
        })
    },
    collapseAll (node) {
      if (!node) return
      node.descendants().forEach(d => {
        if (d !== node && d.children) {
          d._children = d.children
          d.children = null
        }
      })
      if (this.data) this._render()
    },
    expand (node) {
      if (!node) return
      if (node._children) {
        node.children = node._children
        node._children = null
      }
      if (this.data) this._render()
      this.$emit('expand', { element: node, data: node.data })
    }
  }
}
</script>

<style scoped>
.d3tree-container {
  position: relative;
}
</style>
